"use client";

import React, { useMemo } from "react";
import { DollarSign, Package, Calendar, CheckCircle, Clock, XCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CollapsibleSection } from "@/components/shared/CollapsibleSection";
import { RealmBadge } from "@/components/shared/quickbooks/RealmBadge";
import { ClampedText } from "@/components/shared/ClampedText";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { RecurringTransaction } from "@/lib/types/recurring-transactions";

export const RecurringTransactionDetailsTab = React.memo(
  ({ item }: TabContentProps<RecurringTransaction>) => {
    if (!item) {
      return <div className="text-sm text-gray-500">Loading transaction details...</div>;
    }

    const totalAmount = useMemo(() => {
      const amount = Number(item.amount) || 0;
      const quantity = Number(item.quantity) || 1;
      return amount * quantity;
    }, [item.amount, item.quantity]);

    const syncStatusConfig = useMemo(() => {
      switch (item.sync_status) {
        case "synced":
          return {
            color: "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800",
            icon: <CheckCircle className="h-4 w-4" />,
            label: "Synced",
          };
        case "failed":
          return {
            color: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800",
            icon: <XCircle className="h-4 w-4" />,
            label: "Failed",
          };
        case "pending":
        default:
          return {
            color: "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-800",
            icon: <Clock className="h-4 w-4" />,
            label: "Pending",
          };
      }
    }, [item.sync_status]);

    const lastSyncedDisplay = useMemo(() => {
      if (item.last_synced_at) {
        const date = new Date(item.last_synced_at);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      return null;
    }, [item.last_synced_at]);

    const createdDisplay = useMemo(() => {
      if (item.created_at) {
        const date = new Date(item.created_at);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
      return null;
    }, [item.created_at]);

    return (
      <div className="w-full space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{item.item}</h2>
          {item.description && (
            <ClampedText
              text={item.description}
              lines={4}
              className="text-gray-600 dark:text-gray-400 mt-1"
            />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Amount per Unit</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    ${Number(item.amount).toFixed(2)}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-blue-500 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Quantity</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {Number(item.quantity) || 1}
                  </p>
                </div>
                <Package className="h-8 w-8 text-purple-500 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card className="md:col-span-2 bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200 dark:from-blue-950/40 dark:to-blue-900/40 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-blue-700 dark:text-blue-300 font-medium mb-1">Total Monthly Amount</p>
                  <p className="text-3xl font-bold text-blue-900 dark:text-blue-200">
                    ${totalAmount.toFixed(2)}
                  </p>
                </div>
                <DollarSign className="h-10 w-10 text-blue-600 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        </div>

        <CollapsibleSection title="QuickBooks Sync Status" defaultCollapsed>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge
                  variant="outline"
                  className={`text-sm font-medium px-3 py-1 ${syncStatusConfig.color} flex items-center gap-1 whitespace-nowrap`}
                >
                  {syncStatusConfig.icon}
                  {syncStatusConfig.label}
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                {lastSyncedDisplay && (
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Last Synced</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{lastSyncedDisplay}</p>
                    </div>
                  </div>
                )}

                {item.last_synced_month && (
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Last Synced Month</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.last_synced_month}</p>
                    </div>
                  </div>
                )}

                {item.quickbooks_id && (
                  <div className="flex items-start gap-3">
                    <Package className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">QuickBooks ID</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 font-mono">{item.quickbooks_id}</p>
                    </div>
                  </div>
                )}

                {createdDisplay && (
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Created</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{createdDisplay}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
        </CollapsibleSection>

        <CollapsibleSection title="Additional Information" defaultCollapsed>
            <div className="space-y-3">
              {item.quickbooks_realm_id && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Billing Company</span>
                  <RealmBadge realmId={item.quickbooks_realm_id} />
                </div>
              )}
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-600 dark:text-gray-400">Transaction ID</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 font-mono">{item.id}</span>
              </div>
            </div>
        </CollapsibleSection>
      </div>
    );
  }
);

RecurringTransactionDetailsTab.displayName = "RecurringTransactionDetailsTab";
